/**
 * Bunny Level - Activity 1: Animal Journey
 * Help Benny the Bunny hop home!
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bunny } from './Bunny';
import { useNarration } from '../../../hooks/useNarration';
import { animations } from '../../../lib/animationVariants';
import { VoiceEnableButton } from '../../ui/VoiceEnableButton';
import { soundManager } from '../../../lib/soundManager';
import { useLanguage } from '../../../contexts/LanguageContext';
import { track } from '@vercel/analytics';

type GameStage = 'intro' | 'playing' | 'complete';

interface BunnyLevelProps {
  onBackToHome?: () => void;
}

export function BunnyLevel({ onBackToHome }: BunnyLevelProps) {
  const { language } = useLanguage();

  // Translations for all UI text
  const translations = {
    en: {
      home: 'Home',
      listening: 'Listening...',
      tapToHop: 'Tap to Hop!',
      goHome: 'Get home safe',
      youDidIt: 'You Did It!',
      homeMessage: "Benny is home safe!",
      amazingJob: 'Amazing Job!',
      playAgain: 'Play Again!',
    },
    es: {
      home: 'Inicio',
      listening: 'Escuchando...',
      tapToHop: '¡Toca para Saltar!',
      goHome: '¡Llega a casa a salvo!',
      youDidIt: '¡Lo Lograste!',
      homeMessage: '¡Beni está en casa a salvo!',
      amazingJob: '¡Trabajo Increíble!',
      playAgain: '¡Jugar Otra Vez!',
    },
  };

  const t = translations[language];

  // Mobile-specific positioning
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const START_POSITION = isMobile ? -10 : 10; // Start much farther left on mobile
  const END_POSITION = isMobile ? 75 : 90; // End position adjusted for mobile
  const HOPS_TO_WIN = 8; // Number of hops to reach home
  const HOP_DISTANCE = isMobile ? 11 : 10; // Slightly longer hops on mobile

  const [stage, setStage] = useState<GameStage>('intro');
  const [hops, setHops] = useState(0);
  const [position, setPosition] = useState(START_POSITION);
  const [isHopping, setIsHopping] = useState(false);

  // Narration hooks - using selected language
  const { isPlaying: introPlaying } = useNarration({
    scriptId: 'bunny_intro',
    autoPlay: stage === 'intro',
    onComplete: () => setStage('playing'),
    language,
  });

  const { speak: celebrateFirstHop } = useNarration({
    scriptId: 'bunny_first_hop',
    autoPlay: false,
    language,
  });

  const { speak: celebrateComplete } = useNarration({
    scriptId: 'bunny_complete',
    autoPlay: false,
    language,
  });

  // Track game start
  useEffect(() => {
    if (stage === 'playing') {
      track('Game Started', {
        game: 'bunny-level',
        gameName: 'Help Benny Get Home',
        language: language
      });
    }
  }, [stage, language]);

  // Check if bunny reached home
  useEffect(() => {
    if (hops >= HOPS_TO_WIN && stage === 'playing') {
      setStage('complete');
      celebrateComplete();

      // Track game completion
      track('Game Completed', {
        game: 'bunny-level',
        gameName: 'Help Benny Get Home',
        hopsCompleted: hops,
        language: language
      });

      // Play celebration sounds!
      soundManager.playCelebration();
      soundManager.playSparkleSequence(15, 100);
    }
  }, [hops, stage, celebrateComplete, language]);

  const handleTap = () => {
    if (stage !== 'playing' || isHopping) return;

    setIsHopping(true);

    // Play hop sound!
    soundManager.playHop();

    // First hop celebration
    if (hops === 0) {
      celebrateFirstHop();
    }

    // Move bunny forward
    const newPosition = Math.min(position + HOP_DISTANCE, END_POSITION);
    setPosition(newPosition);
    setHops(prev => prev + 1);
  };

  const handleGoHome = () => {
    if (stage !== 'playing' || isHopping) return;

    // Move bunny directly to home position
    setPosition(END_POSITION);
    setHops(HOPS_TO_WIN);

    // Play celebration sound
    soundManager.playCelebration();
  };

  const handleHopComplete = () => {
    setIsHopping(false);
  };

  const handleReplay = () => {
    setStage('intro');
    setHops(0);
    setPosition(START_POSITION);
    setIsHopping(false);
  };

  return (
    <div className="relative w-full h-full overflow-y-auto overflow-x-hidden">
      {/* Background elements - OUTSIDE scaled container so they always fill screen */}
      {/* Sky section - bright cyan blue like the book */}
      <div className="fixed inset-0 w-full h-full bg-cyan-400" />

      {/* Ground section - yellow-green grass with wavy top edge */}
      <div className="fixed bottom-0 left-0 right-0 w-full h-1/2 bg-gradient-to-b from-lime-300 to-lime-400" />

      {/* Wavy/ondulating edge between sky and ground */}
      <svg
        className="fixed left-0 right-0 w-full"
        style={{ bottom: '50%', transform: 'translateY(50%)' }}
        height="60"
        viewBox="0 0 1440 60"
        preserveAspectRatio="none"
      >
        <path
          d="M0,30 Q360,10 720,30 T1440,30 L1440,60 L0,60 Z"
          fill="#bef264"
          opacity="0.8"
        />
        <path
          d="M0,35 Q360,20 720,35 T1440,35 L1440,60 L0,60 Z"
          fill="#a3e635"
          opacity="0.6"
        />
      </svg>

      {/* Mobile viewport scaling wrapper - ensures full game is visible on mobile */}
      <style>{`
        @media (max-width: 768px) {
          .game-container {
            /* Reduced scaling for better mobile experience */
            transform: scale(0.85);
            transform-origin: center center;
          }
          .mobile-hide {
            display: none !important;
          }
          .bunny-home-mobile {
            right: 0% !important;
            width: 300px !important;
            height: 300px !important;
          }
          .beni-position-mobile {
            /* Increase Beni size on mobile */
          }
          .beni-position-mobile img {
            width: 350px !important;
            height: 500px !important;
          }
          .completion-modal-mobile {
            max-width: 100% !important;
            margin: 0 !important;
            padding: 3rem !important;
            border-radius: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
          }
        }
        @media (max-width: 480px) {
          .game-container {
            /* Less aggressive scaling - was 0.5, now 0.75 */
            transform: scale(0.75);
            transform-origin: center center;
          }
          .mobile-hide {
            display: none !important;
          }
          .bunny-home-mobile {
            right: 3% !important;
            width: 280px !important;
            height: 280px !important;
          }
          .beni-position-mobile {
            /* Increase Beni size even more on small phones */
          }
          .beni-position-mobile img {
            width: 380px !important;
            height: 550px !important;
          }
          .completion-modal-mobile {
            max-width: 100% !important;
            margin: 0 !important;
            padding: 2rem !important;
            border-radius: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
          }
        }
      `}</style>

      {/* Voice enable button - positioned in top right */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 40 }}>
        <VoiceEnableButton />
      </div>

      <div className="game-container relative w-full min-h-screen">

      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Sun with rays */}
        <div className="absolute top-12 left-20">
          {/* Sun rays */}
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute top-10 left-10 w-1 h-16 bg-white/70 origin-bottom"
              style={{
                transform: `rotate(${i * 45}deg)`,
              }}
              animate={{
                opacity: [0.5, 0.8, 0.5],
                scaleY: [1, 1.2, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.1,
                ease: 'easeInOut',
              }}
            />
          ))}
          {/* Sun circle */}
          <motion.div
            className="relative w-20 h-20 rounded-full bg-white shadow-lg"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        {/* Subtle floating sparkles */}
        {Array.from({ length: 5 }).map((_, i) => (
          <motion.div
            key={`sparkle-${i}`}
            className="absolute text-3xl opacity-40 mobile-hide"
            style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${10 + Math.random() * 30}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.2, 0.4, 0.2],
              scale: [0.9, 1.1, 0.9],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'easeInOut',
            }}
          >
            ✨
          </motion.div>
        ))}

        {/* Clouds - SVG versions floating across sky */}
        <motion.div
          className="absolute top-16 left-[10%]"
          animate={{ x: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/clouds-01.svg?v=3" alt="cloud" className="w-32 h-24 object-contain opacity-90" />
        </motion.div>
        <motion.div
          className="absolute top-24 right-[20%] mobile-hide"
          animate={{ x: [0, -25, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/clouds-02.svg?v=3" alt="cloud" className="w-40 h-28 object-contain opacity-85" />
        </motion.div>
        <motion.div
          className="absolute top-32 left-[40%] mobile-hide"
          animate={{ x: [0, 20, 0], y: [0, -10, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/clouds-01.svg?v=3" alt="cloud" className="w-36 h-26 object-contain opacity-80" />
        </motion.div>
        <motion.div
          className="absolute top-20 right-[45%] mobile-hide"
          animate={{ x: [0, -20, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/clouds-02.svg?v=3" alt="cloud" className="w-38 h-30 object-contain opacity-75" />
        </motion.div>
        <motion.div
          className="absolute top-12 left-[65%] mobile-hide"
          animate={{ x: [0, 15, 0], y: [0, 8, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/clouds-01.svg?v=3" alt="cloud" className="w-28 h-20 object-contain opacity-70" />
        </motion.div>

        {/* Background trees - strategic placement for depth */}
        <div className="absolute bottom-[34%] mobile-hide" style={{ left: 'calc(12% - 45px)' }}>
          <img src="/tree1.svg?v=3" alt="tree" className="w-[280px] h-[460px] object-contain opacity-55" />
        </div>
        <div className="absolute bottom-[36%] mobile-hide" style={{ left: 'calc(35% - 105px)' }}>
          <img src="/tree5.svg?v=3" alt="tree" className="w-[240px] h-[420px] object-contain opacity-50" />
        </div>
        <div className="absolute bottom-[35%] mobile-hide" style={{ right: 'calc(18% + 150px)' }}>
          <img src="/tree6.svg?v=3" alt="tree" className="w-[260px] h-[450px] object-contain opacity-52" />
        </div>

        {/* Ground layer - cleaner composition inspired by reference */}
        <div className="absolute bottom-0 left-0 right-0 h-1/2">
          {/* Foreground trees - fewer, more intentional placement */}
          <div className="absolute bottom-0 left-[15%] mobile-hide">
            <img src="/tree2.svg?v=3" alt="tree" className="w-[480px] h-[720px] object-contain" />
          </div>
          <div className="absolute bottom-0 mobile-hide" style={{ right: 'calc(5% - 195px)' }}>
            <img src="/tree4.svg?v=3" alt="tree" className="w-[440px] h-[680px] object-contain" />
          </div>

          {/* Flowers - clustered near trees and pathways for cleaner composition */}
          {/* Left cluster - near left tree */}
          <div className="absolute bottom-[2%] left-[8%]" style={{transform: 'rotate(-3deg)'}}>
            <img src="/flower1.svg?v=3" alt="flower" className="w-32 h-32 object-contain" />
          </div>
          <div className="absolute bottom-[5%] left-[12%] mobile-hide" style={{transform: 'rotate(4deg)'}}>
            <img src="/flower2.svg?v=3" alt="flower" className="w-28 h-28 object-contain" />
          </div>
          <div className="absolute bottom-[8%] left-[18%] mobile-hide" style={{transform: 'rotate(-2deg)'}}>
            <img src="/flower3.svg?v=3" alt="flower" className="w-30 h-30 object-contain" />
          </div>
          <div className="absolute bottom-[3%] left-[20%] mobile-hide" style={{transform: 'rotate(5deg)'}}>
            <img src="/flower4.svg?v=3" alt="flower" className="w-26 h-26 object-contain" />
          </div>
          <div className="absolute bottom-[11%] left-[10%] mobile-hide" style={{transform: 'rotate(-4deg)'}}>
            <img src="/flower5.svg?v=3" alt="flower" className="w-28 h-28 object-contain" />
          </div>
          <div className="absolute bottom-[6%] left-[25%] mobile-hide" style={{transform: 'rotate(3deg)'}}>
            <img src="/flower6.svg?v=3" alt="flower" className="w-32 h-32 object-contain" />
          </div>

          {/* Center pathway cluster - fewer flowers for clear path */}
          <div className="absolute bottom-[2%] left-[40%] mobile-hide" style={{transform: 'rotate(-5deg)'}}>
            <img src="/flower7.svg?v=3" alt="flower" className="w-34 h-34 object-contain" />
          </div>
          <div className="absolute bottom-[4%] left-[48%] mobile-hide" style={{transform: 'rotate(4deg)'}}>
            <img src="/flower8.svg?v=3" alt="flower" className="w-28 h-28 object-contain" />
          </div>
          <div className="absolute bottom-[7%] right-[45%] mobile-hide" style={{transform: 'rotate(-3deg)'}}>
            <img src="/flower9.svg?v=3" alt="flower" className="w-30 h-30 object-contain" />
          </div>

          {/* Right cluster - near right tree and home */}
          <div className="absolute bottom-[2%] right-[8%]" style={{transform: 'rotate(5deg)'}}>
            <img src="/flower10.svg?v=3" alt="flower" className="w-36 h-36 object-contain" />
          </div>
          <div className="absolute bottom-[5%] right-[14%] mobile-hide" style={{transform: 'rotate(-4deg)'}}>
            <img src="/flower11.svg?v=3" alt="flower" className="w-30 h-30 object-contain" />
          </div>
          <div className="absolute bottom-[8%] right-[18%] mobile-hide" style={{transform: 'rotate(3deg)'}}>
            <img src="/flower12.svg?v=3" alt="flower" className="w-28 h-28 object-contain" />
          </div>
          <div className="absolute bottom-[3%] right-[22%] mobile-hide" style={{transform: 'rotate(-2deg)'}}>
            <img src="/flower1.svg?v=3" alt="flower" className="w-32 h-32 object-contain" />
          </div>
          <div className="absolute bottom-[11%] right-[10%] mobile-hide" style={{transform: 'rotate(4deg)'}}>
            <img src="/flower2.svg?v=3" alt="flower" className="w-28 h-28 object-contain" />
          </div>
          <div className="absolute bottom-[6%] right-[25%] mobile-hide" style={{transform: 'rotate(-5deg)'}}>
            <img src="/flower3.svg?v=3" alt="flower" className="w-30 h-30 object-contain" />
          </div>

          {/* Rocks - simplified placement */}
          <div className="absolute bottom-[4%] left-[32%] mobile-hide">
            <img src="/rocks1.svg?v=3" alt="rocks" className="w-[100px] h-[100px] object-contain" />
          </div>
          <div className="absolute bottom-[3%] right-[35%] mobile-hide">
            <img src="/rocks2.svg?v=3" alt="rocks" className="w-[120px] h-[120px] object-contain" />
          </div>

          {/* Plants - strategic accents */}
          <div className="absolute bottom-[5%] left-[28%] mobile-hide">
            <img src="/plant1.svg?v=3" alt="plant" className="w-[160px] h-[160px] object-contain" />
          </div>
          <div className="absolute bottom-[7%] right-[30%] mobile-hide">
            <img src="/plant2.svg?v=3" alt="plant" className="w-[140px] h-[140px] object-contain" />
          </div>

          {/* Grass - minimal accents */}
          <div className="absolute bottom-[4%] left-[5%] mobile-hide">
            <img src="/grass1.svg?v=3" alt="grass" className="w-[120px] h-[120px] object-contain" />
          </div>
          <div className="absolute bottom-[6%] right-[5%] mobile-hide">
            <img src="/grass3.svg?v=3" alt="grass" className="w-[130px] h-[130px] object-contain" />
          </div>

          {/* Pond - more central and visible like reference */}
          <div className="absolute bottom-[46%] left-[38%] mobile-hide">
            <img src="/pond1.svg?v=3" alt="pond" className="w-[320px] h-[160px] object-contain" />
          </div>

          {/* Animated Snail! */}
          <motion.div
            className="absolute bottom-[8%] left-[28%] mobile-hide"
            animate={{ x: [0, 150, 0] }}
            transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
          >
            <img src="/snail1.svg?v=3" alt="snail" className="w-16 h-16 object-contain" />
          </motion.div>
        </div>

        {/* Bunny's home (burrow) - at end position where Benny finishes */}
        <motion.div
          className="absolute bottom-0 right-[5%] bunny-home-mobile"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-madriguera.svg?v=3" alt="Bunny's burrow home" className="w-[400px] h-[400px] object-contain" />
        </motion.div>
      </div>

      {/* Narration indicator - positioned below Home button */}
      <AnimatePresence>
        {introPlaying && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-24 left-8 bg-white/90 px-6 py-3 rounded-full shadow-lg flex items-center gap-2"
          >
            <span className="text-4xl">🔊</span>
            <span className="font-semibold text-gray-700">{t.listening}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress circles */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 flex gap-3">
        {Array.from({ length: HOPS_TO_WIN }).map((_, i) => {
          // Harmonized with Benny's colors: orange, blue, red theme
          const colors = [
            'bg-orange-500',
            'bg-blue-500',
            'bg-red-500',
            'bg-orange-400',
            'bg-blue-400',
            'bg-red-400',
            'bg-orange-600',
            'bg-blue-600'
          ];
          return (
            <motion.div
              key={i}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className={`w-12 h-12 rounded-full border-4 border-white shadow-lg ${
                i < hops ? colors[i] : 'bg-gray-300'
              }`}
            />
          );
        })}
      </div>

      {/* Bunny character */}
      <Bunny
        position={position}
        isHopping={isHopping}
        isAtHome={stage === 'complete'}
        onHopComplete={handleHopComplete}
      />

      {/* Tap area */}
      {stage === 'playing' && (
        <motion.button
          onClick={handleTap}
          className="absolute bottom-0 left-0 right-0 h-64 bg-transparent cursor-pointer touch-target"
          whileTap={{ scale: 0.98 }}
          disabled={isHopping}
        >
          <motion.div
            className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-orange-500/90 text-white px-8 py-4 rounded-full text-4xl font-bold shadow-lg"
            animate={animations.pulse}
          >
            {t.tapToHop} 🐾
          </motion.div>
        </motion.button>
      )}

      {/* Go Home Button - prominent orange button */}
      {stage === 'playing' && (
        <motion.button
          onClick={handleGoHome}
          className="absolute bottom-8 right-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-12 py-6 rounded-full text-5xl font-bold shadow-2xl z-40"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          animate={{
            boxShadow: [
              "0 10px 30px rgba(249, 115, 22, 0.5)",
              "0 10px 50px rgba(249, 115, 22, 0.8)",
              "0 10px 30px rgba(249, 115, 22, 0.5)"
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          disabled={isHopping}
        >
          {t.goHome} 🏠
        </motion.button>
      )}

      {/* Completion celebration - ENHANCED! */}
      <AnimatePresence>
        {stage === 'complete' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gradient-to-b from-orange-300/70 to-blue-300/70 flex items-center justify-center z-50"
          >
            <motion.div
              className="bg-white rounded-3xl p-16 max-w-2xl mx-4 text-center space-y-8 shadow-2xl completion-modal-mobile"
              initial={{ scale: 0.5, rotate: -10 }}
              animate={{
                scale: 1,
                rotate: 0,
                y: [0, -10, 0]
              }}
              transition={{
                scale: { type: "spring", bounce: 0.5, duration: 0.8 },
                y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
            >
              {/* Celebration emojis ring */}
              <div className="relative">
                {['🎉', '⭐', '🎊', '✨', '🌟', '💫', '🎈', '🌈'].map((emoji, i) => (
                  <motion.div
                    key={i}
                    className="absolute text-8xl"
                    style={{
                      left: '50%',
                      top: '50%',
                    }}
                    animate={{
                      x: Math.cos((i / 8) * Math.PI * 2) * 120,
                      y: Math.sin((i / 8) * Math.PI * 2) * 120,
                      rotate: 360,
                      scale: [1, 1.3, 1],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: i * 0.1,
                      ease: "easeInOut"
                    }}
                  >
                    {emoji}
                  </motion.div>
                ))}

                <motion.div
                  className="text-9xl"
                  animate={{
                    rotate: [0, -15, 15, -15, 15, 0],
                    scale: [1, 1.2, 1, 1.2, 1]
                  }}
                  transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
                >
                  🐰
                </motion.div>
              </div>

              <motion.h2
                className="text-8xl font-bold text-orange-600"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity, repeatDelay: 0.5 }}
              >
                {t.youDidIt}
              </motion.h2>

              <motion.p
                className="text-5xl text-gray-700 font-semibold"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {t.homeMessage} 🏠✨
              </motion.p>

              <motion.div
                className="text-7xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                🌟 {t.amazingJob} 🌟
              </motion.div>

              <motion.div
                className="flex gap-6 justify-center pt-4 flex-wrap"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <motion.button
                  onClick={handleReplay}
                  className="touch-target bg-gradient-to-r from-orange-500 to-blue-500 text-white px-12 py-6 rounded-full text-5xl font-bold shadow-2xl"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    boxShadow: [
                      "0 10px 30px rgba(249, 115, 22, 0.5)",
                      "0 10px 50px rgba(249, 115, 22, 0.8)",
                      "0 10px 30px rgba(249, 115, 22, 0.5)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {t.playAgain} 🔄
                </motion.button>

                {onBackToHome && (
                  <motion.button
                    onClick={onBackToHome}
                    className="touch-target bg-gradient-to-r from-green-500 to-teal-500 text-white px-12 py-6 rounded-full text-5xl font-bold shadow-2xl"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      boxShadow: [
                        "0 10px 30px rgba(34, 197, 94, 0.5)",
                        "0 10px 50px rgba(34, 197, 94, 0.8)",
                        "0 10px 30px rgba(34, 197, 94, 0.5)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {t.home} 🏠
                  </motion.button>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confetti on completion - ENHANCED! */}
      {stage === 'complete' && (
        <div className="fixed inset-0 pointer-events-none z-40">
          {/* Main confetti burst */}
          {Array.from({ length: 80 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-6xl"
              initial={{
                x: Math.random() * window.innerWidth,
                y: -50,
                rotate: 0,
              }}
              animate={{
                y: window.innerHeight + 50,
                rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
                x: Math.random() * window.innerWidth,
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                delay: Math.random() * 0.8,
                ease: 'linear',
              }}
            >
              {['🎊', '🎉', '⭐', '✨', '🌟', '💫', '🎈', '🌈', '🦋', '🌸'][Math.floor(Math.random() * 10)]}
            </motion.div>
          ))}

          {/* Sparkle bursts from corners */}
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={`sparkle-${i}`}
              className="absolute text-7xl"
              initial={{
                x: i % 2 === 0 ? 0 : window.innerWidth,
                y: i % 4 < 2 ? 0 : window.innerHeight,
                scale: 0,
                rotate: 0,
              }}
              animate={{
                x: window.innerWidth / 2 + (Math.random() - 0.5) * 300,
                y: window.innerHeight / 2 + (Math.random() - 0.5) * 300,
                scale: [0, 1.5, 0],
                rotate: 360,
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 2,
                delay: i * 0.05,
                ease: 'easeOut',
              }}
            >
              ✨
            </motion.div>
          ))}

          {/* Firework explosions */}
          {Array.from({ length: 15 }).map((_, i) => (
            <motion.div
              key={`firework-${i}`}
              className="absolute text-8xl"
              initial={{
                x: Math.random() * window.innerWidth,
                y: window.innerHeight / 3,
                scale: 0,
              }}
              animate={{
                scale: [0, 2, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 1.5,
                delay: Math.random() * 2,
                ease: 'easeOut',
              }}
            >
              💥
            </motion.div>
          ))}
        </div>
      )}
      </div> {/* Close game-container */}
    </div>
  );
}
